import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import LockOutlineIcon from "@mui/icons-material/LockOutline";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { styled } from "@mui/material/styles";
import Cookies from "universal-cookie";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { ItalicRedTypography } from "../_helpers/customMUI";
import { LifeSafer } from "../_helpers/BrandConstants";
import { verifyEmail, verifyText, verifyEnrollee } from "../_api/usersApi";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useToaster } from "../_components/toaster/MuiToasterProvider";
import {
  phoneNumberVerifyMask,
  emailFormat,
} from "../_helpers/PhoneNumberHelper";
import { brandColors } from "../_components/brandedComponent/BrandedColors";
import { loadEnrollee, five9Text } from "../_redux/actions/enrolleeActions";

const VerifySecurityCode = ({ loadEnrollee, five9Text, phoneNumber }) => {
  const location = useLocation();
  const toast = useToaster();
  const navigate = useNavigate();
  const { enrollee, lifeSaferUser } = location.state || {};
  const [submitting, setSubmitting] = useState(false);
  const [resendCode, setResendCode] = useState(false);
  const [sendType, setSendType] = useState(
    enrollee?.resetMethod === "EMAIL" ? "email" : "text"
  );
  const [failedAttempts, setFailedAttempts] = useState(0);
  const cookies = new Cookies();
  const brand = cookies.get("lsEnrollmentReferral") || LifeSafer;
  const { t } = useTranslation();
  const maskedNumber = lifeSaferUser?.CellPhone || lifeSaferUser?.cellPhone;
  const emailMasked = lifeSaferUser?.Email || lifeSaferUser?.email;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      verificationCode: null,
    },
  });

  const StyledFormControlLabel = styled(FormControlLabel)(() => ({
    ".MuiFormControlLabel-label": { fontSize: "15px !important" },
    ".MuiRadio-root": { color: `${brandColors[brand].secondary} !important` },
  }));

  const ButtonUpdate = styled(Button)(() => ({
    backgroundColor: "#fff",
    color: `${brandColors[brand].primary}`,
    fontWeight: 600,
  }));

  const handleVerify = async (data) => {
    const code = data.verificationCode;
    setSubmitting(true);

    try {
      const enrolleeDataUser = {
        userID: enrollee?.userId || enrollee?.UserId,
        resetCode: code,
        brandIdentifier: brand,
      };

      const response = await verifyEnrollee(enrolleeDataUser);

      if(response){
        cookies.set("lifesaferEnrollment", JSON.stringify(response), {
            path: "/",
            sameSite: "strict",
          });
      }

      if (response?.message) {
        toast.error(response?.message);
      }

      if (response) {
        sessionStorage.setItem("enrolleeUser", JSON.stringify(response));
      }

      if (response?.jwtToken) {
        const clientId = enrollee?.clientId;
        const userId = enrollee?.userId;
        const enrolleeId = enrollee?.enrolleeId;

        try {
          await five9Text({
            enrolleeID: enrolleeId,
            messageType: "WelcomeBack",
          });
        } catch (error) {
          console.error("five9Text failed:", error);
        }

        let enrolleeData = null;
        if (clientId !== undefined && userId !== undefined) {
          try {
            enrolleeData = await loadEnrollee(clientId, userId);

            if (enrolleeData) {
              sessionStorage.setItem(
                "enrolleeUser",
                JSON.stringify(enrolleeData)
              );
            }
          } catch (error) {
            console.error("loadEnrollee failed:", error);
          }
        }

        setResendCode(false);
        return navigate("/welcome-back", {
          state: {
            enrolleeData,
            step: enrolleeData?.step,
            story: enrolleeData?.story,
          },
        });
      } else {
        const nextAttempt = failedAttempts + 1;
        setFailedAttempts(nextAttempt);

        if (nextAttempt >= 3) {
          return navigate("/contact-page");
        }
      }
    } catch (error) {
      console.error("Verification error:", error);
      toast.error("Verification failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSend = async () => {
    setSubmitting(true);
    let phoneData = {
      isPhone: true,
      phoneNumber: lifeSaferUser?.CellPhone?.replace(/\D/g, ""),
      emailAddress: null,
      brandIdentifier: brand,
    };

    let emailData = {
      emailAddress: lifeSaferUser?.Email,
      phoneNumber: null,
      isPhone: false,
      brandIdentifier: brand,
    };
    try {
      if (sendType === "text") {
        await verifyText(phoneData);
        toast.success(t("toasts.codeSentSuccess"));
      } else {
        await verifyEmail(emailData);
        toast.success(t("toasts.codeSentSuccess"));
      }
      setResendCode(false);
    } catch (error) {
      console.error("Send failed", error);
      let errMess = error.message;
        errMess = errMess
          .split(":")
          .pop()
          .replace('"', "")
          .slice(0, -2);
        toast.error(errMess);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexGrow: 1,
        alignItems: "center",
        justifyContent: "center",
        marginTop: "100px",
      }}
    >
      <Paper
        elevation={2}
        sx={{ height: 300, width: 340, mx: "auto", overflow: "auto" }}
      >
        <Grid container direction="column">
          <Grid>
            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 1 }}
            >
              <Grid display="flex" alignItems="center" gap={1}>
                <LockOutlineIcon sx={{ color: "#1c355e" }} />
                <Typography variant="h6" fontWeight={900}>
                  {t("steps.verificationCode")}
                </Typography>
              </Grid>
              {resendCode && (
                <CloseIcon
                  onClick={() => setResendCode(false)}
                  sx={{ color: "#d1495b", fontSize: 28, cursor: "pointer" }}
                />
              )}
            </Grid>
            <Divider />

            <Grid sx={{ p: 2 }}>
              {!resendCode ? (
                <>
                  <Typography
                    sx={{
                      fontSize: 15,
                      textAlign: "center",
                      color: `${brandColors[brand].primary}`,
                    }}
                  >
                    {t("steps.welcomeBackMsg")}{" "}
                    {enrollee?.resetSent === "EMAIL" || sendType === "email"
                      ? t("steps.verificationEmail")
                      : t("steps.textMessage")}
                    . {t("steps.enterCode")}
                    <span
                      onClick={() => setResendCode(true)}
                      style={{ textDecoration: "underline", cursor: "pointer" }}
                    >
                      {" "}
                      {t("steps.noReceive")}
                    </span>
                  </Typography>

                  <Grid sx={{ mt: 2, textAlign: "center" }}>
                    <TextField
                      label="Verification Code"
                      size="small"
                      error={!!errors.verificationCode}
                      helperText={errors.verificationCode?.message || ""}
                      disabled={submitting}
                      {...register("verificationCode", {
                        required: t("steps.verificationRequired"),
                        pattern: {
                          value: /^[0-9]{1,6}$/,
                        },
                        maxLength: {
                          value: 6,
                        },
                        onChange: (e) => {
                          e.target.value = e.target.value.replace(/\D/g, "");
                        },
                      })}
                      inputProps={{
                        maxLength: 6,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        onInput: (e) => {
                          e.target.value = e.target.value.replace(/\D/g, "");
                          setValue("verificationCode", e.target.value);
                        },
                      }}
                    />
                  </Grid>

                  <Grid container justifyContent="center" mt={3}>
                    <Button
                      variant="contained"
                      size="medium"
                      onClick={handleSubmit(handleVerify)}
                      disabled={submitting}
                    >
                      {t("steps.continue")}
                    </Button>
                  </Grid>
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      textAlign: "center",
                      fontSize: 15,
                      color: `${brandColors[brand].primary}`,
                    }}
                  >
                    {t("steps.resendVerification")}
                  </Typography>

                  <RadioGroup
                    value={sendType}
                    onChange={(e) => setSendType(e.target.value)}
                  >
                    {(enrollee?.resetMethod === "TEXT" ||
                      enrollee?.resetMethod === "BOTH") && (
                      <>
                        <StyledFormControlLabel
                          value="text"
                          control={<Radio />}
                          label={`${t("steps.sendTextMessage")}${" "}${
                            maskedNumber
                              ? phoneNumberVerifyMask(maskedNumber)
                              : ""
                          }`}
                        />
                        <ItalicRedTypography
                          sx={{ fontSize: 10, ml: 4, fontWeight: 400 }}
                        >
                          {t("steps.textCharges")}
                        </ItalicRedTypography>
                      </>
                    )}
                    {(enrollee?.resetMethod === "EMAIL" ||
                      enrollee?.resetMethod === "BOTH") && (
                      <StyledFormControlLabel
                        value="email"
                        sx={{ lineBreak: "anywhere" }}
                        control={<Radio />}
                        label={`${t("steps.sendEmailTo")} ${
                          emailMasked ? emailFormat(emailMasked) : ""
                        }`}
                      />
                    )}
                  </RadioGroup>

                  <Grid
                    container
                    justifyContent="space-around"
                    alignItems="center"
                    mt={3}
                  >
                    <ButtonUpdate
                      variant="contained"
                      startIcon={<PhoneIphoneIcon />}
                      component="a"
                      // href={`tel:${t(`brandInfo.brandPhone.${brand}`).replace(
                      //   /\D/g,
                      //   ""
                      // )}`}
                      href={`tel:${phoneNumber}`}
                    >
                      {/* {t(`brandInfo.brandPhone.${brand}`)} */}
                      {phoneNumber}
                    </ButtonUpdate>

                    <Button
                      variant="contained"
                      size="medium"
                      startIcon={<SendIcon />}
                      onClick={handleSend}
                      disabled={submitting}
                    >
                      {t("steps.send")}
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

VerifySecurityCode.propTypes = {
  phoneMasked: PropTypes.string,
  emailMasked: PropTypes.string,
  enrollee: PropTypes.object,
  five9Text: PropTypes.func,
  loadEnrollee: PropTypes.func,
  phoneNumber: PropTypes.string,
};

// export default VerifySecurityCode;
const mapDispatchToProps = {
  five9Text,
  loadEnrollee,
};

export default connect(null, mapDispatchToProps)(VerifySecurityCode);
